import { useAllVendors } from "hooks/use-all-vendor-data";
import { getUniqueObjects } from "helpers/data/get-unique-objects";
import { VendorFilter } from "types/filters";

export const getVendorsForSignatureEventLocations = (
    signatureEventLocations: { id: string | undefined }[]
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    return vendors.filter((vendor) => {
        return vendor.signatureEventDetails?.some((detail) =>
            signatureEventLocations.some(
                (location) => location.id === detail?.id
            )
        );
    });
};

export const getVendorsForSignatureEventUniversalTag = (
    signatureEventId: string,
    universalTagId: string
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    return vendors.filter((vendor) => {
        return vendor.signatureEventDetails?.some(
            (detail) =>
                detail?.signatureEvent?.id === signatureEventId &&
                vendor.universalTags?.some((tag) => tag?.id === universalTagId)
        );
    });
};

export const getVendorsForBuilding = (
    buildingId: string
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    return vendors.filter((vendor) => {
        return vendor.signatureEventDetails?.some((detail) => {
            return detail?.locations?.some((location) => {
                const isTargetBuilding =
                    location?.__typename === "DatoCmsBuilding" &&
                    location.id === buildingId;
                const isLocationInBuilding =
                    location?.__typename === "DatoCmsLocation" &&
                    location.building?.id === buildingId;

                return isTargetBuilding || isLocationInBuilding;
            });
        });
    });
};

export const getVendorsForFilters = (
    filters: VendorFilter[],
    signatureEventId?: string
): Queries.VendorFieldsFragment[] => {
    // if there are no filters and no signature event, return all activities
    if (filters.length === 0 && !signatureEventId) {
        return useAllVendors();
    }

    // if there are no filters, return all activities for signature event
    if (filters.length === 0 && signatureEventId) {
        return useAllVendors().filter((vendor) => {
            return vendor.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            );
        });
    }

    const vendorsForUniversalTags = getVendorsForUniveralTags(
        filters,
        signatureEventId
    );

    const vendorsForDietaryOptions = getVendorsForDietaryOptions(
        filters,
        signatureEventId
    );

    const vendorsForCuisines = getVendorsForCuisines(filters, signatureEventId);

    const commonVendors = getCommonVendors(
        vendorsForUniversalTags,
        vendorsForDietaryOptions,
        vendorsForCuisines
    );

    return commonVendors;
};

const getVendorsForUniveralTags = (
    filters: VendorFilter[],
    signatureEventId?: string
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    // get filters where __typename = DatoCmsUniversalTag
    const universalTagFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsUniversalTag"
    );

    if (universalTagFilters.length === 0) {
        return [];
    }

    return vendors.filter((vendor) => {
        // check for signatureEventId
        if (
            signatureEventId &&
            !vendor.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this vendor
        }

        return vendor.universalTags?.some((tag) => {
            return universalTagFilters.some((filter) => filter.id === tag?.id);
        });
    });
};

const getVendorsForDietaryOptions = (
    filters: VendorFilter[],
    signatureEventId?: string
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    // get filters where __typename = DatoCmsDietaryOption
    const dietaryOptionFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsDietaryOption"
    );

    if (dietaryOptionFilters.length === 0) {
        return [];
    }

    return vendors.filter((vendor) => {
        // check for signatureEventId
        if (
            signatureEventId &&
            !vendor.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this vendor
        }

        return vendor.dietaryOptionsAvailable?.some((option) => {
            return dietaryOptionFilters.some(
                (filter) => filter.id === option?.id
            );
        });
    });
};

const getVendorsForCuisines = (
    filters: VendorFilter[],
    signatureEventId?: string
): Queries.VendorFieldsFragment[] => {
    const vendors = useAllVendors();

    // get filters where __typename = DatoCmsCuisine
    const cuisineFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsCuisine"
    );

    if (cuisineFilters.length === 0) {
        return [];
    }

    return vendors.filter((vendor) => {
        // check for signatureEventId
        if (
            signatureEventId &&
            !vendor.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this vendor
        }

        return vendor.cuisines?.some((option) => {
            return cuisineFilters.some((filter) => filter.id === option?.id);
        });
    });
};

function getCommonVendors(
    ...vendorArrays: Queries.VendorFieldsFragment[][]
): Queries.VendorFieldsFragment[] {
    // Filter out empty arrays
    const nonEmptyArrays = vendorArrays.filter(
        (array) => array.length > 0
    );
    
    if (nonEmptyArrays.length === 0) return [];

    // Extract ids from each array
    const idSets = nonEmptyArrays.map(
        (array) => new Set(array.map((vendor) => vendor.id))
    );

    // Find common ids
    const commonIds = [...idSets[0]].filter((id) =>
        idSets.every((set) => set.has(id))
    );

    // Filter vendors to only include those with common ids
    const commonVendors = nonEmptyArrays[0].filter((vendor) =>
        commonIds.includes(vendor.id)
    );

    // Remove any duplicate vendors which existed in multiple input arrays
    return getUniqueObjects(commonVendors);
}